/**
 * Catálogo de pantallas del portal: el vocabulario con el que el asistente puede
 * llevar al cliente de una pantalla a otra.
 *
 * Antes esta tabla vivía dentro de GuidedTour y el agente NO la conocía, así que
 * inventaba el destino de los pasos `navigate` ("Menú lateral > Clientes"), el tour
 * no lo resolvía a ninguna ruta y el recorrido guiado se quedaba congelado, sin UI
 * y sin avanzar. Ahora el catálogo viaja al agente en cada consulta y el agente
 * responde con la RUTA exacta.
 *
 * Es el ESPEJO del menú de `components/layout/AppLayout.tsx` — nombres y rutas
 * copiados de `allNavigation` / `allCompanyNav` / `allManagementNav` / `allSupportNav`,
 * con los mismos roles. Si agregás una entrada al sidebar, agregala acá o el
 * asistente no podrá llevar al cliente a esa pantalla.
 */

/** Roles normalizados, igual que `ROLES` en AppLayout (mayúsculas, sin espacios). */
export const SCREEN_ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  ADMINISTRATOR: 'ADMINISTRATOR',
  COMPANY_ADMIN: 'COMPANYADMIN',
  NAOS_ADMINISTRATOR: 'NAOSADMINISTRATOR',
} as const;

/** Los cuatro roles con acceso administrativo (los que ven Dashboard, Reportes, etc.). */
const ADMINS: string[] = [
  SCREEN_ROLES.SUPERADMIN,
  SCREEN_ROLES.ADMINISTRATOR,
  SCREEN_ROLES.COMPANY_ADMIN,
  SCREEN_ROLES.NAOS_ADMINISTRATOR,
];

export interface Screen {
  name: string;
  route: string;
  /** Roles que ven la pantalla; 'all' = todos. Espeja `roles` del item de menú. */
  roles?: string[] | 'all';
}

/**
 * Pantallas navegables, con el nombre que el cliente ve en el menú.
 *
 * NO incluye:
 * - `/invoices` ni `/pages/invoice/:id`: existen como rutas pero no son entradas de
 *   menú (el detalle de factura se abre desde el listado, con su id).
 * - `/subscriptions/plans`: la entrada está comentada en AppLayout
 *   (`SUBSCRIPTIONS_DISABLED`). El portal no tiene planes ni pagos hoy.
 * - Pantallas de sesión (`/login`, `/select-company`, `/reset-password`…): no son
 *   destinos a los que guiar a alguien que ya está trabajando dentro del portal.
 */
export const SCREENS: Screen[] = [
  // Principal
  { name: 'Dashboard', route: '/dashboard', roles: ADMINS },
  { name: 'Facturas Activas', route: '/home/main', roles: 'all' },
  { name: 'Archivo Histórico', route: '/home/minor', roles: ADMINS },
  { name: 'Nueva Factura', route: '/invoices/new', roles: 'all' },
  { name: 'Clientes', route: '/customers', roles: 'all' },
  { name: 'Productos', route: '/products', roles: 'all' },
  { name: 'Reportes', route: '/report/reports', roles: ADMINS },
  // Empresa
  { name: 'Mi Empresa', route: '/company/edit', roles: ADMINS },
  { name: 'Configuración', route: '/company/settings', roles: ADMINS },
  // Administración
  { name: 'Sucursales', route: '/branchoffice/manager', roles: ADMINS },
  { name: 'Gestión de Usuarios', route: '/user/manager', roles: ADMINS },
  { name: 'Banners', route: '/admin/banners', roles: [SCREEN_ROLES.SUPERADMIN] },
  { name: 'Herramientas de Desarrollo', route: '/admin/devtools', roles: [SCREEN_ROLES.SUPERADMIN] },
  // Soporte
  { name: 'Manual de Usuario', route: '/manual', roles: 'all' },
  { name: 'Preguntas Frecuentes', route: '/faq', roles: 'all' },
];

export const normalize = (s?: string) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

/** Mismo criterio que `hasAccess` de AppLayout. */
const puedeVer = (roles: Screen['roles'], userRole?: string): boolean => {
  if (!userRole) return true; // sin rol conocido, no filtramos
  if (!roles || roles === 'all') return true;
  return roles.includes(normalize(userRole).toUpperCase().replace(/\s+/g, ''));
};

/**
 * Las pantallas que ESTE usuario puede ver, que son las únicas a las que tiene
 * sentido guiarlo.
 *
 * Mandarle al agente el catálogo completo lo dejaba proponer, por ejemplo, "ve a
 * Reportes" a un vendedor — una pantalla que su menú no muestra. Filtrando por rol,
 * ese paso ni se puede formular: el agente solo conoce lo que el cliente tiene.
 */
export const visibleScreens = (userRole?: string): Screen[] =>
  SCREENS.filter((s) => puedeVer(s.roles, userRole)).map(({ name, route }) => ({ name, route }));

/** Nombre (y alias frecuentes) → ruta. Los alias cubren cómo lo nombra la gente. */
const NAV_ROUTES: Record<string, string> = {
  ...Object.fromEntries(SCREENS.map((s) => [normalize(s.name), s.route])),
  facturas: '/home/main',
  'facturas emitidas': '/home/main',
  historico: '/home/minor',
  archivo: '/home/minor',
  'emitir factura': '/invoices/new',
  'crear factura': '/invoices/new',
  clientes: '/customers',
  configuracion: '/company/settings',
  certificados: '/company/settings',
  usuarios: '/user/manager',
  manual: '/manual',
  faq: '/faq',
};

const ROUTES = new Set(Object.values(NAV_ROUTES));

/**
 * Resuelve el destino de un paso `navigate` a una ruta real.
 *
 * Tolerante a propósito: aunque el agente ya recibe el catálogo y debería devolver
 * la ruta, un modelo igual puede describir de más ("la sección de Clientes"). Antes
 * eso era una guía muerta; acá es un match aproximado.
 */
export function resolveRoute(target?: string): string | null {
  if (!target) return null;
  if (target.startsWith('/')) return target;

  // "Menú lateral > Clientes" → "Clientes": el último tramo es la pantalla.
  const raw = target.includes('>') ? target.split('>').pop() : target;
  const n = normalize(raw);
  if (!n) return null;
  if (NAV_ROUTES[n]) return NAV_ROUTES[n];

  let best: { route: string; score: number } | null = null;
  for (const [name, route] of Object.entries(NAV_ROUTES)) {
    let score = 0;
    if (n.includes(name)) score = 80 - (n.length - name.length) * 0.1;
    else if (name.includes(n) && n.length >= 4) score = 70 - (name.length - n.length) * 0.1;
    if (score > 0 && (!best || score > best.score)) best = { route, score };
  }
  return best?.route ?? null;
}

/** ¿La ruta actual ya es la pantalla destino? Evita navegaciones redundantes. */
export const isCurrentRoute = (route: string) =>
  typeof window !== 'undefined' && window.location.pathname === route;

export const isKnownRoute = (route: string) => ROUTES.has(route);
